import { CPLAT_API_ROUTES, useSuspenseFetchs } from '@/api';
import LinkGuide from '@/components/organisms/LinkGuide';
import MarketGuideImageCard from '@/components/organisms/MarketGuideImageCard';
import SmartCommerceSolutionGuide from '@/components/organisms/SmartCommerceSolutionGuide';
import ChangePasswordContainer from '@/containers/market/ChangePasswordContainer';
import MARKET_GUIDE_DATA, { type GuideImage, type GuideLink } from '@/contents/marketGuide';
import { useToast } from '@/hooks/useToast';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';
import { MarketInfo, RegisteredMarketInfo } from '@/types/api';
import { type AxiosResponse } from 'axios';
import { ChevronLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
const ChangeMarketPasswordPage = () => {
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const paramsData = useParams();
  const { marketName } = paramsData;

  const { toast, toasts } = useToast();

  const safeBase64Decode = (str = '') => {
    try {
      return decodeURIComponent(escape(atob(decodeURIComponent(str))));
    } catch (e) {
      return '';
    }
  };

  // const id = atob(params.get('id') || '');
  const idParam = params.get('id') || '';

  let id = '';

  if (idParam) {
    try {
      id = safeBase64Decode(idParam);
    } catch (e) {
      toast({ title: '잘못된 접근입니다.', variant: 'error' });
      navigate(ROUTES.market);
      return null;
    }
  }

  const marketEnName = location.pathname.split('/').at(-1);

  if (!marketEnName) {
    navigate(ROUTES.error);
    return null;
  }

  const guideDatas = MARKET_GUIDE_DATA[marketEnName];

  // const guide = guideDatas.find((guide) => guide.type === 'linkGuide') as GuideLink;

  const [{ data: marketInfoRes }, { data: registeredMarketRes }] = useSuspenseFetchs([
    { url: CPLAT_API_ROUTES.getMarketModel, option: { method: 'POST' } },
    {
      url: CPLAT_API_ROUTES.getRegisteredMarketInfo,
      option: {
        method: 'POST',
        params: {
          cplatToken: user.CplatToken,
        },
      },
    },
    { url: CPLAT_API_ROUTES.getMarketModel, option: { method: 'POST' } },
  ]);

  const { data: marketInfoList } = marketInfoRes as AxiosResponse<MarketInfo[]>;
  const { data: list } = registeredMarketRes as AxiosResponse<
    {
      RegisteredMarketList: RegisteredMarketInfo[];
    }[]
  >;

  const registeredMarketList = list[0].RegisteredMarketList;

  const registeredMarket = registeredMarketList.find(
    (registeredMarket) =>
      registeredMarket.MarketNameEnglish === marketEnName &&
      (registeredMarket.MarketId === id ||
        registeredMarket.VendorId === id ||
        registeredMarket.MarketAlias === id), // ← 반드시 비교!
  ) as RegisteredMarketInfo;

  const marketInfo = marketInfoList.find(
    (info) => info.MarketNameEnglish === marketEnName,
  ) as MarketInfo;

  const shouldRedirectToMarket = !registeredMarket || !marketInfo;

  useEffect(() => {
    if (!shouldRedirectToMarket) return;

    if (toasts.length === 0) {
      toast({ title: '존재하지 않는 마켓입니다.', variant: 'error' });
    }

    navigate(ROUTES.market, { replace: true });
  }, [navigate, shouldRedirectToMarket, toast, toasts.length]);

  if (shouldRedirectToMarket) {
    return null;
  }

  const { MarketName, MarketNameEnglish } = marketInfo as MarketInfo;

  const marketGuide = marketName ? MARKET_GUIDE_DATA[marketName] : undefined;

  const imgGuide = (marketGuide ?? []).filter((guide) => guide.type === 'guideImg') as GuideImage[];
  const linkGuide = (marketGuide ?? []).filter(
    (guide) => guide.type === 'linkGuide',
  ) as GuideLink[];

  const isEmptyGuideImg = imgGuide.length <= 0;

  const handleBack = () => {
    navigate(ROUTES.market);
  };

  // 외부에서 마켓 연동을 해야 하는 마켓 분기 처리 현재 (스마트스토어, 샵바이, 카페 24)
  const externalRegisterMarketList = ['smartstore', 'shopby', 'cafe24', 'imweb'];
  const isExternalMarket = externalRegisterMarketList.includes(marketEnName);
  return (
    <div className=" flex h-full w-full  flex-1 flex-col gap-[16px] bg-gr-50 ">
      <div className="mx-auto flex h-full w-full max-w-full flex-1 flex-col gap-[16px] p-[40px] px-[60px]">
        <div className=" flex h-fit w-full flex-wrap gap-[18px] pb-[40px]">
          <div className="flex flex-1 flex-col">
            <div className="sticky top-0 w-full  ">
              <button
                onClick={handleBack}
                className="mb-[24px] flex w-full cursor-pointer gap-[12px]  text-gr-900 Title2S24"
              >
                <ChevronLeft className="h-8 w-8" />
                뒤로가기
              </button>
              <ChangePasswordContainer
                marketInfo={marketInfo}
                registeredMarket={registeredMarket}
                isEmptyGuideImg={isEmptyGuideImg}
                isExternalMarket={isExternalMarket}
              />
            </div>
          </div>

          <div className="w-full  flex-1 md:mt-[56px] ">
            <div className=" space-y-[30px]">
              {MarketName === '스마트스토어' && <SmartCommerceSolutionGuide />}
              {!isExternalMarket &&
                linkGuide.map((guide) => <LinkGuide guide={guide} key={guide.contents.linkUrl} />)}
              {isEmptyGuideImg || isExternalMarket ? null : (
                <>
                  {imgGuide.map(({ guideImageUrl }) => {
                    return (
                      <MarketGuideImageCard
                        key={guideImageUrl}
                        guideImageUrl={guideImageUrl}
                        marketName={MarketName}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeMarketPasswordPage;
